import { Alert, Box, Button, Container, Stack, Typography } from "@mui/material";
import { Component, ReactNode } from "react";

interface AuthErrorBoundaryProps {
  children: ReactNode;
}

interface AuthErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class AuthErrorBoundary extends Component<AuthErrorBoundaryProps, AuthErrorBoundaryState> {
  constructor(props: AuthErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): AuthErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error) {
    console.error("Auth Error Boundary caught error:", error);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleRelogin = () => {
    // Clear any cached auth state and reload
    window.location.href = window.location.origin;
  };

  render() {
    if (this.state.hasError) {
      const isAuthError =
        this.state.error?.message.includes("auth") ||
        this.state.error?.message.includes("token") ||
        this.state.error?.message.includes("login");

      return (
        <Container maxWidth="sm">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "100vh",
              py: 4,
            }}
          >
            <Stack spacing={3} width="100%">
              <Alert severity="error">
                <Typography variant="h6" gutterBottom>
                  {isAuthError ? "Authentication Error" : "Application Error"}
                </Typography>
                <Typography variant="body2">
                  {this.state.error?.message || "An unexpected error occurred. Please try again."}
                </Typography>
              </Alert>

              <Stack direction="row" spacing={2} justifyContent="center">
                <Button variant="contained" onClick={this.handleRetry}>
                  Retry
                </Button>
                {isAuthError && (
                  <Button variant="outlined" onClick={this.handleRelogin}>
                    Re-login
                  </Button>
                )}
              </Stack>

              {process.env.NODE_ENV === "development" && this.state.error && (
                <Alert severity="info">
                  <Typography variant="body2" fontFamily="monospace">
                    {this.state.error.stack}
                  </Typography>
                </Alert>
              )}
            </Stack>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}
